import React, {
  memo, useEffect, useRef, useState,
} from 'react';

import style from './style.module.scss';
import Grid from './Grid';
import Map, { useMapView } from './Map';

const EMPTY_TILES = [];

/** Mount region mosaic only when near the Map viewport (stadium scale). */
const LazySlot = ({ children, className, style: slotStyle }) => {
  const ref = useRef(null);
  const { viewportEl } = useMapView();
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setActive(true);
      },
      { root: viewportEl || null, rootMargin: '40%', threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [viewportEl]);

  return (
    <div ref={ref} className={className} style={slotStyle}>
      {active ? children : <div className={style.slotPlaceholder} />}
    </div>
  );
};

/** Box/SRS visibility is CSS on the preview root — not props into every slot.
 *  Stadium passes showBoxes always-on so hit-testing stays live whenever labels
 *  are selected; the eye toggle only flips CSS visibility via data-boxes-show. */
const StadiumSheet = memo(({
  preview, onOpenDetail, loading,
}) => {
  const { stageW, stageH, slots } = preview.stadiumLayout || { stageW: 0, stageH: 0, slots: [] };

  return (
    <div className={style.stadiumStage} style={{ width: stageW, height: stageH }}>
      {slots.map((slot, i) => (
        <LazySlot
          key={slot.scanId || `slot-${i}`}
          className={style.stadiumSlot}
          style={{
            left: slot.left,
            top: slot.top,
            width: slot.width,
            height: slot.height,
            transform: `translate(-50%, -50%) rotate(${slot.angle}deg)`,
          }}
        >
          <Grid
            scan={slot.scan}
            tiles={slot.tiles}
            rows={slot.rows}
            cols={slot.cols}
            tileBoxes={preview.tileBoxes}
            selectedLabels={preview.selectedLabelSet}
            showBoxes
            onOpenDetail={onOpenDetail}
            loading={loading}
            boxKeyPrefix={slot.scanId}
            scanKey={slot.scanId || ''}
            variant="slot"
            slotWidth={slot.width}
            slotHeight={slot.height}
            overlay={preview.overlay}
          />
        </LazySlot>
      ))}
    </div>
  );
});

const Preview = ({ data, onOpenDetail }) => {
  const {
    preview, ui, loading, controls,
  } = data;
  const srsAttr = ui.srsShow ? '1' : '0';
  const boxesAttr = ui.showBoxes ? '1' : '0';

  if (preview.showStadium) {
    return (
      <div
        className={style.previewPane}
        data-srs-show={srsAttr}
        data-boxes-show={boxesAttr}
      >
        <Map key={controls.optionValue} fit="contain">
          <StadiumSheet
            preview={preview}
            onOpenDetail={onOpenDetail}
            loading={loading.scan}
          />
        </Map>
      </div>
    );
  }

  if (!preview.showRegion) return null;

  return (
    <div
      className={style.previewPaneHug}
      data-srs-show={srsAttr}
      data-boxes-show={boxesAttr}
    >
      <Map key={controls.optionValue} fit="content">
        <Grid
          scan={preview.singleScan}
          tiles={preview.shimmer ? EMPTY_TILES : preview.tiles}
          rows={preview.rows}
          cols={preview.cols}
          tileBoxes={preview.tileBoxes}
          selectedLabels={preview.selectedLabelSet}
          showBoxes
          onOpenDetail={onOpenDetail}
          loading={preview.shimmer}
          scanKey={controls.optionValue || ''}
          overlay={preview.overlay}
        />
      </Map>
    </div>
  );
};

export default Preview;
